'use client';

import * as React from 'react';
import { ChevronsUpDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './Popover2';
import Button from './Button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from './Command';
import { cn } from '~/utils';
import Icon, { type IconName } from './Icon';
import { useI18n } from '~/features/i18n';

export default function ComboBox({
    value,
    options,
    className,
    popoverClassName,
    placeholder,
    searchPlaceHolder,
    icon,
    isLoading,
    hasError,
    onSearch,
    onChange,
    side,
    avoidCollisions = true,
    compact = false,
}: {
    value: string;
    options: { value: string; label: React.ReactNode }[];
    className?: string;
    popoverClassName?: string;
    placeholder: string;
    searchPlaceHolder?: string;
    icon?: IconName;
    isLoading?: boolean;
    /** @default false */
    hasError?: boolean;
    onSearch: (value: string) => void;
    onChange: (value: string) => void;
    side?: React.ComponentProps<typeof PopoverContent>['side'];
    /** @default true */
    avoidCollisions?: boolean;
    /** @default false — renders a smaller-height trigger with tighter padding. */
    compact?: boolean;
}) {
    const i18n = useI18n();
    const [open, setOpen] = React.useState(false);
    const cachedValueLabel = React.useRef<React.ReactNode>(null);
    {
        // Synchronously cache the label

        if (!value) cachedValueLabel.current = null;

        const valueOption = options.find(x => x.value === value);
        if (valueOption) cachedValueLabel.current = valueOption.label;
    }

    const [searchValue, setSearchValue] = React.useState(
        () =>
            (typeof cachedValueLabel.current === 'string' ? cachedValueLabel.current : null) ||
            value,
    );

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    aria-expanded={open}
                    className={cn(
                        'justify-start rounded-4xl bg-white font-normal text-primary shadow-none',
                        compact ? 'h-10 truncate py-0 text-sm' : 'py-5',
                        hasError && 'ring-1 ring-orange-700',
                        className,
                    )}
                >
                    {/* Green only once a value is filled in; neutral while empty. */}
                    {icon ? (
                        <Icon
                            name={icon}
                            className={cn(
                                'mr-2 shrink-0',
                                value ? 'text-secondary' : 'text-primary',
                            )}
                        />
                    ) : null}

                    <span
                        className={cn(
                            'min-w-0 flex-1 text-left',
                            compact && 'truncate whitespace-nowrap',
                        )}
                    >
                        {cachedValueLabel.current ||
                            (isLoading ? (
                                <span className="text-blue-400">
                                    {i18n._('combobox.loading', undefined, {
                                        message: 'Loading…',
                                    })}
                                </span>
                            ) : (
                                value || <span className="text-blue-400">{placeholder}</span>
                            ))}
                    </span>
                    <ChevronsUpDown className="ml-auto h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>

            <PopoverContent
                className={cn('px-3 py-3', popoverClassName)}
                side={side}
                align="start"
                avoidCollisions={avoidCollisions || true}
            >
                <Command shouldFilter={false}>
                    <CommandInput
                        isLoading={isLoading}
                        value={searchValue}
                        onValueChange={val => {
                            setSearchValue(val);
                            onSearch(val);
                        }}
                        className="outline-hidden appearance-none border-0 bg-transparent p-0 py-4 text-primary placeholder:text-blue-400 focus:ring-0"
                        placeholder={searchPlaceHolder}
                    />
                    <CommandEmpty>{i18n._('No options found.')}</CommandEmpty>
                    <CommandList>
                        <CommandGroup>
                            {options.map(option => (
                                <CommandItem
                                    key={option.value}
                                    value={option.value}
                                    // py-1.5 is a ~30px target on touch, below the ~44px
                                    // guideline — options sat close enough together to hit the
                                    // wrong one. Desktop density is left as it was.
                                    className="py-3 sm:py-1.5"
                                    onSelect={(currentValue: string) => {
                                        onChange(currentValue);
                                        setOpen(false);
                                        setSearchValue('');
                                    }}
                                >
                                    {option.label}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
